/**
 * BrandingColorsEditor — the broker brand-colour editor + live preview, shared by the
 * wizard Branding step AND the Administration branding surface (Wave W4 — branding, ui+preview).
 *
 * Three BrandColorFields (Primärfarbe / Sekundärfarbe / Kopfzeilenfarbe — hex input +
 * interactive picker each) → a "Vorschau generieren" button that renders, inline, a live
 * branded customer email + branded portal page from the chosen logo + colours + broker name
 * → a Save button that patches ONLY the changed colours through the existing branding patch.
 *
 * Persist contract (PatchBrandingDto): each colour is a #RRGGBB hex OR null (null clears).
 * An empty field clears the colour (→ null / Brokly default); a valid hex is normalised and
 * sent; a partial/invalid field blocks the save (never patched). Only changed keys are sent.
 *
 * Controlled-once: seeded from `initialColors`; the parent re-seeds by remounting (key) or
 * the local edits stand until saved. Tokens only — Brokly palette, no emerald/green.
 *
 * [Source: packages/shared/src/broker-branding.ts] (colour keys + hex guard)
 * [Source: apps/api/src/modules/platform-admin/dto/patch-tenant.dto.ts] (null-clears merge)
 */

import { useMemo, useState } from 'react';
import { Eye } from 'lucide-react';
import type { BrokerBrandingColorKey, BrokerBrandingColors } from '@brokly/shared';
import { isHexColor } from '@brokly/shared';
import { Button } from '../../../components/ui/Button';
import { BrandColorField } from './BrandColorField';
import { BrandingPreview } from './BrandingPreview';
import { BROKER_COLOR_DEFAULTS, BROKER_COLOR_FIELDS, normalizeHex } from './broker-colors';

/** The patch subset this editor emits — only the changed colour keys (hex or null-clear). */
export type BrandingColorsPatch = Partial<Record<BrokerBrandingColorKey, string | null>>;

/** null/undefined → '' (empty field = "unset / Brokly default"). */
function toRaw(value: string | null | undefined): string {
  return typeof value === 'string' ? value : '';
}

export function BrandingColorsEditor({
  initialColors,
  brokerName,
  logoUrl,
  saving,
  onSave,
  savedNote,
}: {
  initialColors: BrokerBrandingColors;
  brokerName: string;
  logoUrl?: string | null;
  saving: boolean;
  onSave: (patch: BrandingColorsPatch) => void | Promise<void>;
  /** Optional confirmation line shown after a successful save. */
  savedNote?: string | null;
}) {
  const [draft, setDraft] = useState<Record<BrokerBrandingColorKey, string>>(() => ({
    primaryColor: toRaw(initialColors.primaryColor),
    secondaryColor: toRaw(initialColors.secondaryColor),
    headerColor: toRaw(initialColors.headerColor),
  }));
  const [showPreview, setShowPreview] = useState(false);

  const setColor = (key: BrokerBrandingColorKey, hex: string) =>
    setDraft((d) => ({ ...d, [key]: hex }));

  // A field is invalid only when non-empty AND not #RRGGBB (empty = clear, allowed).
  const anyInvalid = BROKER_COLOR_FIELDS.some(
    ({ key }) => draft[key] !== '' && !isHexColor(draft[key]),
  );

  // The live preview reads the current draft: valid → hex, else null (→ Brokly default).
  const previewColors: BrokerBrandingColors = useMemo(
    () => ({
      primaryColor: isHexColor(draft.primaryColor) ? normalizeHex(draft.primaryColor) : null,
      secondaryColor: isHexColor(draft.secondaryColor) ? normalizeHex(draft.secondaryColor) : null,
      headerColor: isHexColor(draft.headerColor) ? normalizeHex(draft.headerColor) : null,
    }),
    [draft],
  );

  // Only CHANGED keys are patched: empty → null (clear); valid → normalised hex.
  // A partial/invalid field is skipped entirely (never sent) — anyInvalid blocks save.
  const patch: BrandingColorsPatch = useMemo(() => {
    const out: BrandingColorsPatch = {};
    for (const { key } of BROKER_COLOR_FIELDS) {
      const raw = draft[key];
      const initial = initialColors[key] ?? null;
      if (raw === '') {
        if (initial !== null) out[key] = null;
      } else if (isHexColor(raw)) {
        const next = normalizeHex(raw);
        if (next !== (initial ?? '').toLowerCase()) out[key] = next;
      }
    }
    return out;
  }, [draft, initialColors]);

  const hasChanges = Object.keys(patch).length > 0;

  return (
    <div className="space-y-5">
      <div className="grid gap-5 sm:grid-cols-3">
        {BROKER_COLOR_FIELDS.map(({ key, label, help }) => (
          <BrandColorField
            key={key}
            label={label}
            help={help}
            value={draft[key]}
            placeholder={BROKER_COLOR_DEFAULTS[key]}
            onChange={(hex) => setColor(key, hex)}
          />
        ))}
      </div>

      <div className="flex flex-wrap items-center gap-2">
        <Button type="button" variant="outline" onClick={() => setShowPreview((s) => !s)}>
          <Eye aria-hidden /> {showPreview ? 'Vorschau ausblenden' : 'Vorschau generieren'}
        </Button>
        <Button
          type="button"
          disabled={saving || anyInvalid || !hasChanges}
          onClick={() => void onSave(patch)}
        >
          {saving ? 'Speichert…' : 'Farben speichern'}
        </Button>
        {anyInvalid && (
          <span className="text-xs text-destructive">
            Bitte gültige Hex-Werte (#RRGGBB) eingeben.
          </span>
        )}
        {savedNote && !hasChanges && (
          <span className="text-xs text-muted-foreground">{savedNote}</span>
        )}
      </div>

      {showPreview && (
        <div className="rounded-lg border border-border bg-card p-4">
          <BrandingPreview name={brokerName} logoUrl={logoUrl} colors={previewColors} />
        </div>
      )}
    </div>
  );
}
